import type { ReactNode } from "react";
import { Navigate } from "react-router";
import { Logo } from "@/components/brand/logo.tsx";
import { ErrorState } from "@/components/common/states.tsx";
import { useMe } from "@/lib/queries.ts";

/** A centered mark while `/api/me` loads. */
export function FullPageLoader() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background" aria-busy>
      <Logo size={40} className="animate-shimmer text-primary dark:text-foreground" />
    </div>
  );
}

/** Lets only signed-in users of an active tenant through: 401 → `/login`, pending or blocked → `/waitlist`. */
export function RequireActive({ children }: { children: ReactNode }) {
  const me = useMe();
  if (me.isPending) return <FullPageLoader />;
  if (me.isError) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <ErrorState error={me.error} onRetry={() => void me.refetch()} />
      </div>
    );
  }
  if (!me.data) return <Navigate to="/login" replace />;
  if (me.data.tenant.status !== "active") return <Navigate to="/waitlist" replace />;
  return <>{children}</>;
}

/** Admins only; everyone else goes to `/app`. */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const me = useMe();
  if (!me.data || me.data.user.role !== "admin") return <Navigate to="/app" replace />;
  return <>{children}</>;
}

/** Sends visitors to the right place from `/`. */
export function RootRedirect() {
  const me = useMe();
  if (me.isPending) return <FullPageLoader />;
  if (!me.data) return <Navigate to="/login" replace />;
  if (me.data.tenant.status !== "active") return <Navigate to="/waitlist" replace />;
  return <Navigate to="/app" replace />;
}
